import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook';

import { ModalTextInput } from './ModalTextInput';

type Props = {
  value?: string;
  options: string[];
  placeholder: string;
  onChange: (value: string) => void;
  onFocus?: VoidFunction;
  onBlur?: VoidFunction;
  'data-testid'?: string;
  disabled?: boolean;
};

export const ModalTextInputDropdown = ({
  'data-testid': testId = 'modal-text-input-dropdown',
  value,
  placeholder,
  options,
  onChange,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  disabled,
}: Props) => {
  const [isFocused, setIsFocused] = useState(false);
  const { disableScope, enableScope } = useHotkeysContext();
  const resetHotkeyScope = useCallback(() => {
    disableScope('text-input-dropdown');
    enableScope('modal');
  }, [enableScope, disableScope]);

  useEffect(() => {
    if (isFocused && !disabled) {
      disableScope('modal');
      enableScope('text-input-dropdown');
    } else {
      resetHotkeyScope();
    }
    return resetHotkeyScope;
  }, [isFocused, enableScope, disableScope, resetHotkeyScope, disabled]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const onBlur = useCallback(
    (evt?: React.FocusEvent<HTMLElement>) => {
      if (evt && evt.relatedTarget) {
        if (dropdownRef.current?.contains(evt.relatedTarget as Node)) {
          evt.preventDefault();
          return;
        }
        onBlurProp?.();
      }
      setIsFocused(false);
    },
    [onBlurProp]
  );

  useHotkeys(
    ['esc'],
    (evt) => {
      evt.preventDefault();
      onBlur();
    },
    {
      enableOnFormTags: true,
      scopes: ['text-input-dropdown'],
    },
    []
  );

  const onFocus = useCallback(() => {
    setIsFocused(true);
    onFocusProp?.();
  }, [onFocusProp]);

  return (
    <div data-testid={testId} className={clsx('h-f2', 'relative')}>
      <ModalTextInput
        data-testid={
          testId ? `${testId}-input` : 'modal-text-input-dropdown-input'
        }
        placeholder={placeholder}
        value={value ?? ''}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={(evt) => onChange(evt.target.value)}
        disabled={disabled}
      />
      {isFocused && !disabled && (
        <div
          ref={dropdownRef}
          data-testid="list-of-options"
          className={clsx(
            'absolute z-30 overflow-y-scroll max-h-f10 scrollbar-hide flex flex-col w-full shadow-nice'
          )}
        >
          {options.map((option) => (
            <button
              type="button"
              key={option}
              className={clsx(
                'text-left h-f2 cursor-pointer w-full',
                'bg-main-200 hover:bg-negative',
                value === option ? 'text-main-500' : 'text-main',
                'first:border-t-0 border-t-[0.5px] border-x-[0.5px] last:border-b-[0.5px] border-main-300'
              )}
              onClick={() => {
                onChange(option);
                setIsFocused(false);
              }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
